"use client";

import { useState, useEffect } from "react";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategory,
  createCategory,
  updateCategory,
  deleteCategory,
} from "@/store/modules/categories/thunks";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { categorySchema } from "@/validations";
import { useApiReady } from "@/hooks/useApiReady";

const useCategory = (onSuccess?: () => void, modelType: string = "") => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { isReady: isApiReady } = useApiReady();
  const dispatch: AppDispatch = useDispatch();
  const [categorySlug, setCategorySlug] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>(params.search || "");

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        model_type: modelType,
      },
    }));
  }, [modelType, setParams]);

  useEffect(() => {
    if (!isApiReady) return;

    if (params.filter?.model_type) {
      dispatch(fetchCategory(params));
    }
  }, [params, dispatch, isApiReady]);

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== params.search) {
        updateParams({
          search: searchValue,
          current_page: 1,
        });
        setCurrentPage(1);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, updateParams, params.search]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(categorySchema),
    defaultValues: {
      name: "",
      model_type: modelType,
    },
  });

  useEffect(() => {
    setValue("model_type", modelType);
  }, [modelType, setValue]);

  const setFormData = (data: Partial<typeof categorySchema.__outputType>) => {
    reset({
      ...data,
      model_type: modelType,
    });
  };

  const onSubmit = async (data: typeof categorySchema.__outputType) => {
    try {
      let result;

      if (categorySlug) {
        result = await dispatch(
          updateCategory({ slug: categorySlug, ...data }),
        );
      } else {
        result = await dispatch(createCategory(data));
      }

      if (
        createCategory.fulfilled.match(result) ||
        updateCategory.fulfilled.match(result)
      ) {
        if (onSuccess) onSuccess();
        reset();
        setCategorySlug(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados da categoria:", error);
    }
  };

  const { categories, loading, error } = useSelector(
    (state: any) => state.categories,
  );

  const totalItems = categories?.total ?? 0;
  const itemsPerPage = categories?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  const [currentPage, setCurrentPage] = useState(categories?.current_page ?? 1);

  const handlePageChange = (page: number) => {
    handlePage(page);
    setCurrentPage(page);
  };

  const handleFilterChange = (value: any) => {
    setSearchValue(value);
  };

  const handleDelete = async () => {
    try {
      if (categorySlug) {
        await dispatch(deleteCategory(categorySlug));
        setCategorySlug(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao deletar categoria:", error);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    categories,
    loading,
    error,
    setCategorySlug,
    handleDelete,
    setFormData,
    params,
    setParams,
    handlePage,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
    handleFilterChange,
    searchValue,
  };
};

export default useCategory;
